import type { MagiMethod } from "../config";
import type { ModelResponse, Phase } from "../types";

/**
 * Overlays drawn inside the chamber for method-specific aggregation cinematics.
 *
 * Data-heavy tallies (VoteYesNo, VoteOptions, Probability) are NOT shown here —
 * they live in VerdictOverlay below the chamber. Compose's ranked drafts live
 * in the ComposeRanking component below the chamber. This file keeps only the
 * small decorative / identity pieces that don't compete with the MAGI core.
 */
export function MethodAnimation({
  method,
  phase,
  responses,
}: {
  method: MagiMethod;
  phase: Phase;
  responses: ModelResponse[];
}) {
  const visible =
    phase === "aggregating" || phase === "rapporteur" || phase === "complete";
  if (!visible) return null;

  switch (method) {
    case "Majority":
      return <SpotlightHighest responses={responses} />;
    case "Consensus":
      return <Halo color="var(--positive)" />;
    case "Minority":
      return <SpotlightLowest responses={responses} />;
    case "VoteYesNo":
    case "VoteOptions":
    case "Probability":
    case "Compose":
    case "Synthesis":
      // Handled elsewhere: Vote*/Probability by VerdictOverlay, Compose by
      // ComposeRanking, Synthesis by the chamber's converge lines + core spin.
      return null;
  }
}

function Halo({ color }: { color: string }) {
  return (
    <div className="method-overlay">
      <div
        style={{
          width: 180,
          height: 180,
          borderRadius: "50%",
          border: `1px solid ${color}`,
          boxShadow: `0 0 40px ${color}`,
          opacity: 0.35,
          animation: "core-pulse 2.2s ease-in-out infinite",
        }}
      />
    </div>
  );
}

function SpotlightHighest({ responses }: { responses: ModelResponse[] }) {
  if (responses.length === 0) return null;
  const pick = [...responses].sort((a, b) => b.confidence_score - a.confidence_score)[0];
  return (
    <div className="method-overlay">
      <div
        style={{
          position: "absolute",
          bottom: 20,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          className="overlay-chip"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            letterSpacing: "0.18em",
            color: "var(--accent)",
          }}
        >
          AMPLIFYING · {pick.model}
        </div>
      </div>
    </div>
  );
}
function SpotlightLowest({ responses }: { responses: ModelResponse[] }) {
  if (responses.length === 0) return null;
  const pick = [...responses].sort((a, b) => a.confidence_score - b.confidence_score)[0];
  return (
    <div className="method-overlay">
      <div
        style={{
          position: "absolute",
          bottom: 20,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          className="overlay-chip"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            letterSpacing: "0.18em",
            color: "var(--negative)",
          }}
        >
          SURFACING DISSENT · {pick.model}
        </div>
      </div>
    </div>
  );
}

